import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import {
  PULL_THRESHOLD,
  MAX_PULL_DISTANCE,
  RESISTANCE_FACTOR,
  REFRESH_HOLD_DISTANCE,
} from "./pullToRefreshConstants";
interface UsePullToRefreshOptions {
  onRefresh: () => Promise<void>;
  isEnabled?: boolean;
}

interface UsePullToRefreshResult {
  pullDistance: number;
  isRefreshing: boolean;
  hasReachedThreshold: boolean;
}

export const usePullToRefresh = (
  scrollContainerRef: React.RefObject<HTMLElement | null>,
  { onRefresh, isEnabled = true }: UsePullToRefreshOptions
): UsePullToRefreshResult => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const touchStartY = useRef(0);
  const touchStartX = useRef(0);
  const isTracking = useRef(false);
  const isHorizontalSwipe = useRef(false);
  const isPullingRef = useRef(false);
  const pullDistanceRef = useRef(0);
  const wasAtTopOnTouchStart = useRef(false);
  const scrollableAncestorRef = useRef<HTMLElement | null>(null);
  const onRefreshEvent = useEffectEvent(onRefresh);

  const hasReachedThreshold = pullDistance >= PULL_THRESHOLD;

  /** Finds the nearest ancestor that actually scrolls, cached on touchstart. */
  const findScrollableAncestor = useCallback(
    (target: EventTarget | null): HTMLElement | null => {
      const container = scrollContainerRef.current;
      if (!container) return null;

      let current: Element | null =
        target instanceof Element ? target : container;
      while (current && current !== container) {
        if (current instanceof HTMLElement) {
          const { overflowY } = getComputedStyle(current);
          if (
            (overflowY === "auto" || overflowY === "scroll") &&
            current.scrollHeight > current.clientHeight
          ) {
            return current;
          }
        }
        current = current.parentElement;
      }
      return container;
    },
    [scrollContainerRef]
  );

  /**
   * Check if the scroll container is scrolled away from the top.
   */
  const isScrolledDown = useCallback((): boolean => {
    const el = scrollableAncestorRef.current;
    return el !== null && el.scrollTop > 0;
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!isEnabled || isRefreshing) return;
      const container = scrollContainerRef.current;
      if (!container) return;

      const touch = e.touches[0];
      if (!touch) return;

      touchStartY.current = touch.clientY;
      touchStartX.current = touch.clientX;
      isTracking.current = true;
      isHorizontalSwipe.current = false;
      scrollableAncestorRef.current = findScrollableAncestor(e.target);
      wasAtTopOnTouchStart.current = !isScrolledDown();
    },
    [
      isEnabled,
      isRefreshing,
      scrollContainerRef,
      findScrollableAncestor,
      isScrolledDown,
    ]
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isTracking.current || !isEnabled || isRefreshing) return;
      const container = scrollContainerRef.current;
      if (!container) return;

      const touch = e.touches[0];
      if (!touch) return;

      const currentY = touch.clientY;
      const currentX = touch.clientX;
      const deltaY = currentY - touchStartY.current;
      const deltaX = currentX - touchStartX.current;

      // On first significant movement, determine if horizontal swipe
      if (!isPullingRef.current && !isHorizontalSwipe.current) {
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
          isHorizontalSwipe.current = true;
          return;
        }
      }

      if (isHorizontalSwipe.current) return;

      // Only pull down when the touch started at the top AND we're still at the top
      if (deltaY > 0 && wasAtTopOnTouchStart.current && !isScrolledDown()) {
        e.preventDefault(); // Prevent native scroll/bounce

        const resistedDistance = Math.min(
          deltaY * RESISTANCE_FACTOR,
          MAX_PULL_DISTANCE
        );

        pullDistanceRef.current = resistedDistance;
        setPullDistance(resistedDistance);
        isPullingRef.current = true;
      } else {
        // User scrolled back up or container is scrolled down
        if (isPullingRef.current) {
          pullDistanceRef.current = 0;
          setPullDistance(0);
          isPullingRef.current = false;
        }
      }
    },
    [isEnabled, isRefreshing, scrollContainerRef, isScrolledDown]
  );

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isEnabled) return;

    const handleTouchEnd = () => {
      if (!isTracking.current) return;
      isTracking.current = false;
      isHorizontalSwipe.current = false;

      if (pullDistanceRef.current >= PULL_THRESHOLD) {
        setIsRefreshing(true);
        pullDistanceRef.current = REFRESH_HOLD_DISTANCE;
        setPullDistance(REFRESH_HOLD_DISTANCE);

        const resetState = () => {
          setIsRefreshing(false);
          pullDistanceRef.current = 0;
          setPullDistance(0);
          isPullingRef.current = false;
        };

        onRefreshEvent().then(resetState, (error) => {
          console.error("[PullToRefresh] Refresh failed:", error);
          resetState();
        });
      } else {
        pullDistanceRef.current = 0;
        setPullDistance(0);
        isPullingRef.current = false;
      }
    };

    container.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    container.addEventListener("touchmove", handleTouchMove, {
      passive: false, // Need to call preventDefault
    });
    container.addEventListener("touchend", handleTouchEnd);
    container.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchmove", handleTouchMove);
      container.removeEventListener("touchend", handleTouchEnd);
      container.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [scrollContainerRef, isEnabled, handleTouchStart, handleTouchMove]);

  return {
    pullDistance,
    isRefreshing,
    hasReachedThreshold,
  };
};
